import { useRef, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Camera, Sparkles, Plus, Trash2, ChevronLeft, Wand2, Info, Image as ImageIcon,
  Tag, FileText, Edit3, Check,
} from 'lucide-react';
import { Button } from '@/components/Button';
import { Loader } from '@/components/Loader';
import { analyzePhoto, analyzeText, analyzeLabel, analyzeHandwritten } from '@/lib/ai';
import { createMeal, enrichMealNutrients } from '@/hooks/useMeals';
import { uploadMealThumb } from '@/lib/mealThumb';
import { useUserStore } from '@/store/userStore';
import { slavaMealReaction, type MealReaction } from '@/utils/slavaMealReaction';
import { ShareRecognition } from '@/components/ShareRecognition';
import type { RecognitionFacts } from '@/utils/recognitionCard';
import { getMealMicros } from '@/utils/mealMicros';
import { mealTitle } from '@/utils/mealTitle';
import { FrequentItems, type FrequentItem } from '@/components/FrequentItems';
import { useTelegram } from '@/hooks/useTelegram';
import { CameraCapture } from '@/components/CameraCapture';
import { AccuracyNote } from '@/components/AccuracyNote';
import { MealItemEditor } from '@/components/MealItemEditor';
import { ComponentsEditor } from '@/components/ComponentsEditor';
import { backfillComponentMacros } from '@/lib/componentMacros';
import { supabase } from '@/lib/supabase';
import { matchReceipt, type ReceiptLine } from '@/utils/receiptMatch';
import type { MealItem, MealType, AIAnalysisResult, MealComponent } from '@/types';
import { fmtNum, mealTypeLabel, getActiveTargets } from '@/utils/format';
import { consumedAtFor, dayPhrase, offsetFromParam } from '@/lib/mealDay';
import { DayStrip } from '@/components/DayStrip';
type Mode = 'photo' | 'text' | 'manual';
type PhotoMode = 'dish' | 'label' | 'handwritten' | null;
const hiddenInputStyle: React.CSSProperties = {
  position: 'absolute',
  left: '-9999px',
  width: 1,
  height: 1,
  opacity: 0,
  pointerEvents: 'none',
};
async function compressPhotoFile(file: File, pm: PhotoMode): Promise<File> {
  const startedAt = performance.now();
  // dish / handwritten — обычное сжатие
  // label — выше качество, чтобы не убить мелкий текст на упаковке
  const MAX_SIZE = pm === 'label' ? 2000 : 1600;
  const JPEG_QUALITY = pm === 'label' ? 0.88 : 0.82;
  const imageBitmap = await createImageBitmap(file);
  const sourceWidth = imageBitmap.width;
  const sourceHeight = imageBitmap.height;
  const scale = Math.min(1, MAX_SIZE / Math.max(sourceWidth, sourceHeight));
  const targetWidth = Math.round(sourceWidth * scale);
  const targetHeight = Math.round(sourceHeight * scale);
  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext('2d', { alpha: false });
  if (!ctx) {
    throw new Error('Canvas 2D context is not available');
  }
  ctx.drawImage(imageBitmap, 0, 0, targetWidth, targetHeight);
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => {
        if (!b) reject(new Error('Failed to compress image'));
        else resolve(b);
      },
      'image/jpeg',
      JPEG_QUALITY
    );
  });
  const compressedFile = new File(
    [blob],
    `food_${Date.now()}.jpg`,
    { type: 'image/jpeg' }
  );
  console.log('[PHOTO_COMPRESS]', {
    mode: pm,
    originalKb: Math.round(file.size / 1024),
    compressedKb: Math.round(compressedFile.size / 1024),
    ratio: Number((compressedFile.size / Math.max(file.size, 1)).toFixed(3)),
    originalSize: `${sourceWidth}x${sourceHeight}`,
    compressedSize: `${targetWidth}x${targetHeight}`,
    quality: JPEG_QUALITY,
    compressionMs: Math.round(performance.now() - startedAt),
  });
  return compressedFile;
}
/**
 * Строки чека → позиции приёма с официальными КБЖУ сети.
 * Правило комбо живёт в matchReceipt: нашли набор в меню — берём его,
 * а вложенные строки глушим, иначе калории удвоятся.
 */
async function receiptToItems(res: {
  chain: string | null;
  items: ReceiptLine[];
}): Promise<any[]> {
  if (!res.chain || !Array.isArray(res.items) || !res.items.length) return [];

  const { data, error } = await supabase
    .from('chain_items')
    .select('id, name, weight_g, kcal, protein_g, fat_g, carbs_g')
    .eq('chain', res.chain);
  if (error || !data?.length) {
    console.error('[RECEIPT] menu load failed:', error);
    return [];
  }

  return matchReceipt(res.items, data as any)
    .filter((m) => m.chosenId && !m.coveredByParent)
    .map((m) => {
      const item = m.candidates.find((c) => c.item.id === m.chosenId)!.item;
      const qty = m.line.qty;
      return {
        name: qty > 1 ? `${item.name} ×${qty}` : item.name,
        estimated_weight_g: Number(item.weight_g) * qty,
        calories: item.kcal * qty,
        protein_g: Number(item.protein_g) * qty,
        fat_g: Number(item.fat_g) * qty,
        carbs_g: Number(item.carbs_g) * qty,
      };
    });
}

function fileToBase64Local(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Failed to read image file'));
    reader.onload = () => {
      const result = String(reader.result || '');
      const base64 = result.includes(',') ? result.split(',')[1] : result;
      resolve(base64);
    };
    reader.readAsDataURL(file);
  });
}
// 🆕 Пропорциональное масштабирование компонентов при изменении веса блюда
function scaleComponents(components: MealComponent[] | undefined, ratio: number): MealComponent[] {
  if (!Array.isArray(components) || components.length === 0) return [];
  return components.map((c) => ({
    name: c.name,
    weight_g: Math.max(1, Math.round(Number(c.weight_g || 0) * ratio)),
    calories: Math.max(0, Math.round(Number(c.calories || 0) * ratio)),
    // 🆕 Пропорциональный пересчёт БЖУ компонентов
    protein_g: +(Math.max(0, Number((c as any).protein_g || 0) * ratio)).toFixed(1),
    fat_g: +(Math.max(0, Number((c as any).fat_g || 0) * ratio)).toFixed(1),
    carbs_g: +(Math.max(0, Number((c as any).carbs_g || 0) * ratio)).toFixed(1),
  } as any));
}
export default function AddMeal() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const initialMode = (params.get('mode') as Mode) ?? 'photo';
  const initialDateParam = params.get('date'); // дата из дневника: yyyy-MM-dd (или старое 'yesterday')
  const { haptic } = useTelegram();
  const { profile } = useUserStore();
  const [mode, setMode] = useState<Mode>(initialMode);
  const [photoMode, setPhotoMode] = useState<PhotoMode>(null);
  const [items, setItems] = useState<MealItem[]>([]);
  const [baseItems, setBaseItems] = useState<MealItem[]>([]);
  const [confidence, setConfidence] = useState<number | undefined>();
  const [showAccuracyHint, setShowAccuracyHint] = useState(false);
  const initialMealType = (params.get('meal_type') as MealType) ?? getDefaultMealType();
  const [mealType, setMealType] = useState<MealType>(initialMealType);
  
  // 📅 День приёма — смещением от сегодня (см. lib/mealDay.ts). Из дневника приходит
  // дата того дня, на который человек смотрит.
  const [dayOffset, setDayOffset] = useState<number>(() => offsetFromParam(initialDateParam));
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  // Сколько секунд занял разбор — цифра, которая и продаёт на карточке
  const [analyzeSeconds, setAnalyzeSeconds] = useState<number | null>(null);
  const [textInput, setTextInput] = useState('');
  const [manualName, setManualName] = useState('');
  const [manualWeight, setManualWeight] = useState('100');
  const [manualCal, setManualCal] = useState('');
  const [saving, setSaving] = useState(false);
  const [showAddExtra, setShowAddExtra] = useState(false);
  const [cameraTrigger, setCameraTrigger] = useState(0);
  const [extraName, setExtraName] = useState('');
  const [extraWeight, setExtraWeight] = useState('100');
  const [labelData, setLabelData] = useState<any>(null);
  const [labelWeight, setLabelWeight] = useState('');
  // Название продукта с этикетки — правится до записи (11.10.2026). Модель часто
  // не видит названия в кадре, продукт уходил в дневник как «Продукт», и найти
  // его потом в истории, чтобы повторить, было нельзя.
  const [labelName, setLabelName] = useState('');
  // 🆕 Редактор продукта через AI
  const [editingItem, setEditingItem] = useState<{ item: MealItem; index: number } | null>(null);
  // 🆕 Toast после сохранения
  // dayNote — «за вчера», «на завтра»…; пусто, если запись на сегодня
  const [savedToast, setSavedToast] = useState<{ kcal: number; mealType: MealType; dayNote: string; reaction: MealReaction | null; reco: RecognitionFacts | null } | null>(null);
  function reset() {
    setItems([]);
    setBaseItems([]);
    setConfidence(undefined);
    setPhotoPreview(null);
    setTextInput('');
    setShowAccuracyHint(false);
    setShowAddExtra(false);
    setExtraName('');
    setExtraWeight('100');
    setPhotoMode(null);
    setLabelData(null);
    setLabelWeight('');
  }
  // 🆕 Полный сброс формы после сохранения (всё кроме mealType и выбранного дня)
  function resetAfterSave() {
    setItems([]);
    setBaseItems([]);
    setConfidence(undefined);
    setPhotoPreview(null);
    setTextInput('');
    setShowAccuracyHint(false);
    setShowAddExtra(false);
    setExtraName('');
    setExtraWeight('100');
    setPhotoMode(null);
    setLabelData(null);
    setLabelWeight('');
    setManualName('');
    setManualCal('');
    setManualWeight('100');
  }
  // Авто-скрытие toast через 4 секунды — но ТОЛЬКО когда в нём нечего нажимать.
  //
  // ЗАЧЕМ ОГОВОРКА (05.09.2026). Слава сообщил, что кнопки «поделиться приёмом
  // пищи» нет. Она БЫЛА: блок рендерился и был выкачен на оба хоста — но жил
  // внутри тоста с этим таймером. Четырёх секунд хватает, чтобы заметить, что
  // внизу что-то мелькнуло, и не хватает, чтобы прочитать, решить и нажать.
  // Снаружи это неотличимо от «кнопки нет вообще».
  //
  // ⚠️ Урок: перенося интерактивный блок в другое место, проверяй не только
  // условие показа, но и ЖИЗНЕННЫЙ ЦИКЛ контейнера. Я сверил всю цепочку
  // (mode, photoMode, photoPreview) и не посмотрел на таймер рядом.
  //
  // Предложение поделиться требует решения, поэтому ждёт человека. Залипнуть
  // тост не может: он закрывается тапом по себе и кнопкой «Готово».
  useEffect(() => {
    if (!savedToast) return;
    if (savedToast.reco) return;
    const timer = setTimeout(() => setSavedToast(null), 4000);
    return () => clearTimeout(timer);
  }, [savedToast]);
  function openCamera(pm: PhotoMode) {
    haptic('medium');
    setPhotoMode(pm);
    // Счётчик, а не флаг: при флаге повторное нажатие после отменённой съёмки
    // не меняло состояние, и камера больше не открывалась до выхода с экрана
    setCameraTrigger((n) => n + 1);
  }
  function openGallery(pm: PhotoMode) {
    haptic('light');
    setPhotoMode(pm);
    galleryInputRef.current?.click();
  }
  async function handlePhotoFromCamera(file: File) {
    if (!photoMode) return;
    await processPhoto(file, photoMode);
  }
  async function handlePhotoFromGallery(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Сбрасываем СРАЗУ, а не после разбора: если разбор упадёт, значение
    // осталось бы прежним и повторный выбор того же файла не сработал бы
    e.target.value = '';
    if (!file || !photoMode) return;
    await processPhoto(file, photoMode);
  }
  async function processPhoto(file: File, pm: PhotoMode) {
    setAnalyzing(true);
    setAnalyzeSeconds(null);
    haptic('medium');
    const totalStartedAt = performance.now();
    try {
      const preparedFile = await compressPhotoFile(file, pm);
      const base64 = await fileToBase64Local(preparedFile);
      console.log('[PHOTO_FLOW]', {
        stage: 'base64_ready',
        mode: pm,
        base64Chars: base64.length,
        approxMb: Number((base64.length * 0.75 / 1024 / 1024).toFixed(2)),
        ms: Math.round(performance.now() - totalStartedAt),
      });
      setPhotoPreview(`data:${preparedFile.type};base64,${base64}`);
      const invokeStartedAt = performance.now();
      if (pm === 'dish') {
        const res = await analyzePhoto(base64);
        console.log('[PHOTO_FLOW]', {
          stage: 'analyzePhoto_done',
          mode: pm,
          items: res.items?.length ?? 0,
          with_components: (res.items ?? []).filter((i: any) => Array.isArray(i.components) && i.components.length > 0).length,
          invokeMs: Math.round(performance.now() - invokeStartedAt),
          totalMs: Math.round(performance.now() - totalStartedAt),
        });
        applyAIResult(res);
        setShowAccuracyHint(true);
      } else if (pm === 'label') {
        const res = await analyzeLabel(base64);
        console.log('[PHOTO_FLOW]', {
          stage: 'analyzeLabel_done',
          mode: pm,
          kind: (res as any).kind ?? 'label',
          found: !!res.found,
          invokeMs: Math.round(performance.now() - invokeStartedAt),
          totalMs: Math.round(performance.now() - totalStartedAt),
        });

        // На фото оказался чек, а не упаковка. Отдельной кнопки для этого нет
        // намеренно: человек просто снимает, приложение разбирается само.
        // Калории берём из справочника сетей, модель их не считает.
        if ((res as any).kind === 'receipt') {
          const items = await receiptToItems(res as any);
          if (!items.length) {
            alert('Чек прочитал, но ни одна позиция не нашлась в меню сети. Добавь вручную.');
            setPhotoPreview(null);
            setPhotoMode(null);
            return;
          }
          applyAIResult({ items } as any);
          setShowAccuracyHint(true);
          haptic('success');
          return;
        }

        if (!res.found) {
          alert('Не вижу таблицу пищевой ценности на фото. Попробуй ещё раз — приблизь камеру к этикетке.');
          setPhotoPreview(null);
          setPhotoMode(null);
          return;
        }
        setLabelData(res);
        setLabelName((res?.product_name || '').trim());
      } else if (pm === 'handwritten') {
        const res = await analyzeHandwritten(base64);
        console.log('[PHOTO_FLOW]', {
          stage: 'analyzeHandwritten_done',
          mode: pm,
          items: res.items?.length ?? 0,
          invokeMs: Math.round(performance.now() - invokeStartedAt),
          totalMs: Math.round(performance.now() - totalStartedAt),
        });
        if (!res.items?.length) {
          alert('Не удалось распознать список. Попробуй сфотографировать чётче.');
          setPhotoPreview(null);
          setPhotoMode(null);
          return;
        }
        applyAIResult(res);
        setShowAccuracyHint(true);
      }
      haptic('success');
    } catch (e) {
      console.error(e);
      haptic('error');
      alert('Не удалось распознать. Попробуй ещё раз или введи вручную.');
    } finally {
      setAnalyzing(false);
      // Время разбора целиком, как его прожил человек: от нажатия до результата.
      setAnalyzeSeconds(+((performance.now() - totalStartedAt) / 1000).toFixed(1));
    }
  }
  function applyLabelData() {
    if (!labelData || !labelWeight) return;
    
    const weight = Number(labelWeight);
    if (!weight || weight < 1) {
      alert('Введи корректный вес');
      return;
    }
    const perGrams = labelData.per_grams || 100;
    const ratio = weight / perGrams;
    const item = {
      name: labelName.trim() || 'Продукт',
      weight_g: weight,
      calories: Math.round(Number(labelData.calories) * ratio),
      protein_g: +(Number(labelData.protein_g) * ratio).toFixed(1),
      fat_g: +(Number(labelData.fat_g) * ratio).toFixed(1),
      carbs_g: +(Number(labelData.carbs_g) * ratio).toFixed(1),
      components: [], // 🆕 У этикеток нет components
    };
    setItems([item]);
    setBaseItems([item]);
    setLabelData(null);
    setLabelWeight('');
    haptic('success');
  }
  async function handleTextAnalyze() {
    if (!textInput.trim()) return;
    setAnalyzing(true);
    haptic('medium');
    try {
      const res = await analyzeText(textInput.trim());
      applyAIResult(res);
      haptic('success');
    } catch (e) {
      console.error(e);
      haptic('error');
      alert('Не удалось распознать. Попробуй переформулировать.');
    } finally {
      setAnalyzing(false);
    }
  }
  function applyAIResult(res: AIAnalysisResult) {
  const mapped = res.items.map((i: any) => ({
    name: i.name,
    weight_g: Number(i.estimated_weight_g) || 100,
    calories: Number(i.calories) || 0,
    protein_g: Number(i.protein_g) || 0,
    fat_g: Number(i.fat_g) || 0,
    carbs_g: Number(i.carbs_g) || 0,
    // Передаём нутриенты дальше
    fiber_g: Number(i.fiber_g) || 0,
    vegetables_fruits_g: Number(i.vegetables_fruits_g) || 0,
    omega3_g: Number(i.omega3_g) || 0,
    saturated_fat_g: Number(i.saturated_fat_g) || 0,
    calcium_mg: Number(i.calcium_mg) || 0,
    iron_mg: Number(i.iron_mg) || 0,
    magnesium_mg: Number(i.magnesium_mg) || 0,
    zinc_mg: Number(i.zinc_mg) || 0,
    potassium_mg: Number(i.potassium_mg) || 0,
    vitamin_d_mcg: Number(i.vitamin_d_mcg) || 0,
    vitamin_b12_mcg: Number(i.vitamin_b12_mcg) || 0,
    vitamin_c_mg: Number(i.vitamin_c_mg) || 0,
    // 🆕 Передаём components для составных блюд (роллы, шаурма и т.д.).
    // Раскладываем макросы блюда по компонентам (FAST-режим их не отдаёт),
    // иначе правка состава обнулит Б/Ж/У.
    components: backfillComponentMacros(
      Array.isArray(i.components) ? i.components : [],
      {
        protein_g: Number(i.protein_g) || 0,
        fat_g: Number(i.fat_g) || 0,
        carbs_g: Number(i.carbs_g) || 0,
      }
    ),
  }));
  console.log('[applyAIResult] mapped items с components:', mapped.map((it: any) => ({
    name: it.name,
    components_count: it.components?.length || 0,
  })));
  setItems(mapped);
  setBaseItems(mapped);
  setConfidence(res.confidence);
}
  function addManualItem() {
    if (!manualName || !manualCal) return;
    haptic('light');
    const w = Number(manualWeight) || 100;
    const c = Number(manualCal) || 0;
    const newItem = {
      name: manualName, weight_g: w, calories: c,
      protein_g: 0, fat_g: 0, carbs_g: 0,
      components: [], // 🆕 У ручных продуктов нет components
    };
    setItems((prev) => [...prev, newItem]);
    setBaseItems((prev) => [...prev, newItem]);
    setManualName('');
    setManualCal('');
    setManualWeight('100');
  }
  // Тап по частому продукту — ноль обращений к модели: КБЖУ берутся
  // из прошлой записи человека. Это и есть экономия, ради которой всё затевалось.
  function addFrequentItem(f: FrequentItem) {
    const newItem = {
      name: f.name,
      weight_g: Number(f.weight_g),
      calories: Number(f.calories),
      protein_g: Number(f.protein_g) || 0,
      fat_g: Number(f.fat_g) || 0,
      carbs_g: Number(f.carbs_g) || 0,
      components: [],
    };
    setItems((prev) => [...prev, newItem]);
    setBaseItems((prev) => [...prev, newItem]);
  }

  async function addExtraItem() {
    if (!extraName.trim()) return;
    haptic('medium');
    setAnalyzing(true);
    try {
      const res = await analyzeText(`${extraName} ${extraWeight}г`);
      const item: any = res.items[0];
      if (item) {
        const newItem = {
          name: extraName,
          weight_g: Number(extraWeight) || 100,
          calories: Number(item.calories) || 0,
          protein_g: Number(item.protein_g) || 0,
          fat_g: Number(item.fat_g) || 0,
          carbs_g: Number(item.carbs_g) || 0,
          // 🆕 Components от AI (с раскладкой макросов по компонентам)
          components: backfillComponentMacros(
            Array.isArray(item.components) ? item.components : [],
            {
              protein_g: Number(item.protein_g) || 0,
              fat_g: Number(item.fat_g) || 0,
              carbs_g: Number(item.carbs_g) || 0,
            }
          ),
        };
        setItems((prev) => [...prev, newItem]);
        setBaseItems((prev) => [...prev, newItem]);
      }
      setShowAddExtra(false);
      setExtraName('');
      setExtraWeight('100');
      haptic('success');
    } catch (e) {
      haptic('error');
    } finally {
      setAnalyzing(false);
    }
  }
  // «Продукт» — заглушка, которую ставит распознавание этикетки без названия
  const isNameless = (name: string) => !name?.trim() || name.trim().toLowerCase() === 'продукт';
  function updateItemName(idx: number, newName: string) {
    setItems((prev) =>
      prev.map((it, i) => i === idx ? { ...it, name: newName } : it)
    );
  }
  function updateItemWeight(idx: number, newWeight: number) {
  const base: any = baseItems[idx];
  if (!base) return;
  const ratio = newWeight / (base.weight_g || 1);
  setItems((prev) =>
    prev.map((it, i) => {
      if (i !== idx) return it;
      return {
        ...it,
        weight_g: newWeight,
        calories: Math.round(Number(base.calories) * ratio),
        protein_g: +(Number(base.protein_g) * ratio).toFixed(1),
        fat_g: +(Number(base.fat_g) * ratio).toFixed(1),
        carbs_g: +(Number(base.carbs_g) * ratio).toFixed(1),
        // Пересчёт микронутриентов пропорционально весу
        fiber_g: +(Number(base.fiber_g || 0) * ratio).toFixed(1),
        vegetables_fruits_g: +(Number(base.vegetables_fruits_g || 0) * ratio).toFixed(1),
        omega3_g: +(Number(base.omega3_g || 0) * ratio).toFixed(2),
        saturated_fat_g: +(Number(base.saturated_fat_g || 0) * ratio).toFixed(1),
        calcium_mg: Math.round(Number(base.calcium_mg || 0) * ratio),
        iron_mg: +(Number(base.iron_mg || 0) * ratio).toFixed(1),
        magnesium_mg: Math.round(Number(base.magnesium_mg || 0) * ratio),
        zinc_mg: +(Number(base.zinc_mg || 0) * ratio).toFixed(1),
        potassium_mg: Math.round(Number(base.potassium_mg || 0) * ratio),
        vitamin_d_mcg: +(Number(base.vitamin_d_mcg || 0) * ratio).toFixed(1),
        vitamin_b12_mcg: +(Number(base.vitamin_b12_mcg || 0) * ratio).toFixed(2),
        vitamin_c_mg: +(Number(base.vitamin_c_mg || 0) * ratio).toFixed(1),
        // 🆕 Пропорциональное масштабирование components
        components: scaleComponents(base.components, ratio),
      };
    }),
  );
}
  // 🆕 Обработчик изменения components — обновляет items И baseItems с пересчётом итогов
  function handleComponentsChange(
    idx: number,
    newComponents: MealComponent[],
    recalcTotals: {
      weight_g: number;
      calories: number;
      protein_g: number;
      fat_g: number;
      carbs_g: number;
    }
  ) {
    setItems((prev) =>
      prev.map((it, i) => {
        if (i !== idx) return it;
        return {
          ...it,
          components: newComponents,
          weight_g: recalcTotals.weight_g,
          calories: recalcTotals.calories,
          protein_g: recalcTotals.protein_g,
          fat_g: recalcTotals.fat_g,
          carbs_g: recalcTotals.carbs_g,
        };
      })
    );
    // 🆕 ВАЖНО: обновляем baseItems тоже — иначе при последующем изменении
    // веса блюда расчёт будет от старых значений
    setBaseItems((prev) =>
      prev.map((it, i) => {
        if (i !== idx) return it;
        return {
          ...it,
          components: newComponents,
          weight_g: recalcTotals.weight_g,
          calories: recalcTotals.calories,
          protein_g: recalcTotals.protein_g,
          fat_g: recalcTotals.fat_g,
          carbs_g: recalcTotals.carbs_g,
        };
      })
    );
  }
  function removeItem(idx: number) {
    haptic('light');
    setItems((prev) => prev.filter((_, i) => i !== idx));
    setBaseItems((prev) => prev.filter((_, i) => i !== idx));
  }
  // Обновление продукта через AI-редактор
  function handleEditorSave(updatedItem: MealItem, index: number) {
    // Обновляем и items, и baseItems (т.к. это новые базовые значения)
    // 🆕 Сохраняем components из текущего item если редактор их не вернул
    const currentItem = items[index];
    const merged: MealItem = {
      ...updatedItem,
      components: (updatedItem as any).components ?? currentItem?.components ?? [],
    };
    setItems((prev) =>
      prev.map((it, i) => i === index ? merged : it)
    );
    setBaseItems((prev) =>
      prev.map((it, i) => i === index ? merged : it)
    );
    setEditingItem(null);
    haptic('success');
  }
  function handleEditorDelete(index: number) {
    removeItem(index);
    setEditingItem(null);
  }
  async function save() {
    if (!items.length) return;
    setSaving(true);
    try {
      // 📅 Сегодня — «сейчас», как и было; любой другой день — в час этого приёма
      const consumedAt = consumedAtFor(dayOffset, mealType);
      
      const totalCal = items.reduce((a, i) => a + Number(i.calories), 0);
      // 🆕 Лог что передаём в createMeal — для диагностики
      console.log('[AddMeal:save] items с components:', items.map((it: any) => ({
        name: it.name,
        components_count: it.components?.length || 0,
      })));
      
      const savedMeal = await createMeal({
        meal_type: mealType,
        source: photoMode === 'label' ? 'manual' : mode,
        // Стёртое название не сохраняем пустым: строка без имени в дневнике не открывается на правку
        items: items.map((it: any) => ({ ...it, name: it.name?.trim() || 'Продукт' })),
        raw_input: mode === 'text' ? textInput : undefined,
        ai_confidence: confidence,
        consumed_at: consumedAt,
      });
      haptic('success');

      // 🆕 Фоновая подгрузка клетчатки/овощей/витаминов/минералов после сохранения.
      // Не блокируем UX — витамины появятся на главном экране через пару секунд.
      if (savedMeal?.id) {
        const mealId = savedMeal.id;
        enrichMealNutrients(mealId)
          .then(async () => {
            // Досчёт закончился — дорисовываем витамины в карточку репоста.
            // Замерено: медиана 3 секунды, 90-й процентиль 4. Тост к этому
            // моменту ещё открыт, потому что таймер его больше не гасит,
            // когда есть что нажать. До сохранения этих цифр не существует
            // в принципе: FAST-режим считает шесть полей ради скорости.
            const micros = await getMealMicros(mealId);
            if (!micros.length) return;
            setSavedToast((t) => (t?.reco ? { ...t, reco: { ...t.reco, micros } } : t));
          })
          .catch((e) => console.error('[AddMeal] enrichMealNutrients failed:', e));
      }

      // Превью блюда — только если человек сам включил в профиле.
      // Снимок берём ДО очистки формы и грузим фоном: ждать миниатюру
      // ради списка в дневнике человек не должен.
      if (savedMeal?.id && profile?.id && (profile as any)?.save_meal_photos && photoPreview) {
        const shot = photoPreview;
        (async () => {
          const path = await uploadMealThumb(savedMeal.id, profile.id, shot);
          if (path) await supabase.from('meals').update({ photo_url: path }).eq('id', savedMeal.id);
        })().catch((e) => console.warn('[AddMeal] thumb failed:', e));
      }
      
      // Реакция Славы на этот приём — по правилам, без модели (см. slavaMealReaction).
      // Считается по дню ВКЛЮЧАЯ только что записанное, поэтому берём свежие итоги.
      let reaction: MealReaction | null = null;
      try {
        if (profile && dayOffset === 0) {
          const t = getActiveTargets(profile);
          const { data: dayRows } = await supabase
            .from('meals')
            .select('total_calories,total_protein_g')
            .eq('user_id', profile.id)
            .gte('consumed_at', new Date().toISOString().slice(0, 10));
          const dayCalories = (dayRows ?? []).reduce((x, r: any) => x + Number(r.total_calories || 0), 0);
          const dayProtein = (dayRows ?? []).reduce((x, r: any) => x + Number(r.total_protein_g || 0), 0);
          reaction = slavaMealReaction({
            calories: Math.round(totalCal),
            protein: items.reduce((x, i: any) => x + Number(i.protein_g || 0), 0),
            dayCalories, dayProtein,
            goalCalories: t.calories, goalProtein: t.protein,
            mealIndex: (dayRows ?? []).length,
            streak: profile.current_streak_meals ?? 0,
            hour: new Date().getHours(),
          }, profile);
        }
      } catch (e) {
        console.warn('[AddMeal] реакция Славы не собралась:', e);
      }

      // Показываем toast и очищаем форму вместо редиректа
      // Факты для карточки берём ДО resetAfterSave: он очищает форму и фото,
      // а карточке нужен снимок.
      const reco: RecognitionFacts | null =
        mode === 'photo' && photoMode === 'dish' && photoPreview
          ? {
              photo: photoPreview,
              title: mealTitle(items),
              calories: Math.round(totalCal),
              protein: items.reduce((x, i: any) => x + Number(i.protein_g || 0), 0),
              fat: items.reduce((x, i: any) => x + Number(i.fat_g || 0), 0),
              carbs: items.reduce((x, i: any) => x + Number(i.carbs_g || 0), 0),
              seconds: analyzeSeconds,
            }
          : null;

      setSavedToast({
        kcal: Math.round(totalCal),
        mealType: mealType,
        dayNote: dayPhrase(dayOffset),
        reaction,
        reco,
      });
      resetAfterSave();
    } catch (e) {
      console.error(e);
      haptic('error');
      alert('Не удалось сохранить.');
    } finally {
      setSaving(false);
    }
  }
  const totals = items.reduce(
    (a, i) => ({
      cal: a.cal + Number(i.calories),
      p: a.p + Number(i.protein_g),
      f: a.f + Number(i.fat_g),
      c: a.c + Number(i.carbs_g),
    }),
    { cal: 0, p: 0, f: 0, c: 0 },
  );
  const hasItems = items.length > 0;
  return (
    // ⚠️ ОТСТУП СНИЗУ СЧИТАЕТСЯ, А НЕ ПОДБИРАЕТСЯ (05.09.2026).
    //
    // Слава: «скролил в самый низ, конечной остановкой была кнопка Сохранить,
    // больше ничего». То есть хвост формы был НЕДОСТИЖИМ физически, а не
    // просто неудобен.
    //
    // Считаем, что перекрывает низ экрана:
    //   нижнее меню          `fixed bottom-0`, 64px
    //   панель «Сохранить»   `fixed bottom-16` = ещё 64px сверху меню,
    //                        плюс pt-3 + кнопка size="lg" + pb-3 ≈ 84px
    //   итого перекрыто      ~148px, и это БЕЗ безопасной зоны телефона
    //
    // Стояло `pb-44` = 176px. Разница 28px — меньше одной строки текста,
    // поэтому «Оценка примерная» и всё, что ниже, оказывались под панелью.
    // На айфоне с домашней полосой не хватало и этих 28px.
    //
    // Теперь 208px плюс безопасная зона устройства. Без items панель
    // «Сохранить» не рисуется вовсе — там достаточно меню и запаса.
    <div
      className="px-5 pt-4 max-w-md mx-auto"
      style={{
        paddingBottom: hasItems
          ? 'calc(13rem + env(safe-area-inset-bottom))'
          : 'calc(8rem + env(safe-area-inset-bottom))',
      }}
    >
      <div className="flex items-center gap-3 mb-5">
        <button
          type="button"
          onClick={() => nav(-1)}
          className="w-10 h-10 rounded-xl bg-bg-card border border-line flex items-center justify-center"
        >
          <ChevronLeft size={20} />
        </button>
        <h1 className="font-display text-xl font-bold">Добавить приём</h1>
      </div>
      <div className="card-elevated p-1 grid grid-cols-3 mb-5">
        {(
          [
            { k: 'photo', label: 'Фото', Icon: Camera },
            { k: 'text', label: 'Текст', Icon: Sparkles },
            { k: 'manual', label: 'Вручную', Icon: Plus },
          ] as const
        ).map(({ k, label, Icon }) => (
          <button
            type="button"
            key={k}
            onClick={() => { setMode(k); reset(); haptic('light'); }}
            className={`flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm transition-all ${
              mode === k ? 'bg-bg text-ink shadow-card' : 'text-ink-muted'
            }`}
          >
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>
      {/* 📅 День приёма — полоска: неделя назад … два дня вперёд */}
      <div className="mb-5">
        <label className="label mb-2 block">{dayOffset > 0 ? 'На какой день' : 'Когда съел'}</label>
        <DayStrip value={dayOffset} onChange={setDayOffset} bleed />
      </div>
      <div className="mb-5">
        <label className="label mb-2 block">Тип приёма</label>
        <div className="grid grid-cols-4 gap-1.5">
          {(['breakfast', 'lunch', 'dinner', 'snack'] as MealType[]).map((mt) => (
            <button
              type="button"
              key={mt}
              onClick={() => setMealType(mt)}
              className={`py-2 rounded-lg text-xs font-medium transition-colors ${
                mealType === mt
                  ? 'bg-emerald-glow text-bg'
                  : 'bg-bg-card text-ink-muted border border-line'
              }`}
            >
              {mealTypeLabel[mt]}
            </button>
          ))}
        </div>
      </div>
      <AnimatePresence mode="wait">
        {/* PHOTO */}
        {mode === 'photo' && (
          <motion.div key="photo" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <input
              ref={galleryInputRef}
              type="file"
              accept="image/*"
              style={hiddenInputStyle}
              onChange={handlePhotoFromGallery}
            />
            {!photoPreview && !analyzing && !labelData && (
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => openCamera('dish')}
                  className="w-full aspect-[4/3] rounded-2xl border-2 border-dashed border-emerald-glow/40 bg-emerald-glow/5 flex flex-col items-center justify-center gap-3 hover:bg-emerald-glow/10 active:scale-[0.98] transition-all"
                >
                  <Camera size={36} className="text-emerald-glow" />
                  <div className="text-emerald-glow font-medium">Сфоткать блюдо</div>
                  <div className="text-xs text-ink-muted px-6 text-center">
                    ИИ распознает продукты на тарелке
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => openCamera('label')}
                  className="w-full p-4 rounded-2xl border border-line bg-bg-card flex items-center gap-3 hover:bg-sky-glow/5 hover:border-sky-glow/30 active:scale-[0.98] transition-all"
                >
                  <div className="w-12 h-12 rounded-xl bg-sky-glow/15 flex items-center justify-center shrink-0">
                    <Tag size={20} className="text-sky-glow" />
                  </div>
                  <div className="flex-1 text-left">
                    <div className="font-medium text-sm">Сфоткать этикетку</div>
                    <div className="text-[11px] text-ink-muted">
                      Точные данные с упаковки продукта
                    </div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => openCamera('handwritten')}
                  className="w-full p-4 rounded-2xl border border-line bg-bg-card flex items-center gap-3 hover:bg-warn/5 hover:border-warn/30 active:scale-[0.98] transition-all"
                >
                  <div className="w-12 h-12 rounded-xl bg-warn/15 flex items-center justify-center shrink-0">
                    <FileText size={20} className="text-warn" />
                  </div>
                  <div className="flex-1 text-left">
                    <div className="font-medium text-sm">Сфоткать список</div>
                    <div className="text-[11px] text-ink-muted">
                      Рукописный или печатный список продуктов
                    </div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => openGallery('dish')}
                  className="w-full py-3 rounded-xl bg-bg-elevated flex items-center justify-center gap-2 hover:bg-line/30 active:scale-[0.98] transition-all text-xs text-ink-muted mt-3"
                >
                  <ImageIcon size={14} />
                  Загрузить блюдо из галереи
                </button>
              </div>
            )}
            {analyzing && (
              <div className="card p-8">
                <Loader label={
                  photoMode === 'label' ? 'Читаю этикетку…' :
                  photoMode === 'handwritten' ? 'Распознаю список…' :
                  'Анализирую блюдо…'
                } />
              </div>
            )}
            {labelData && !analyzing && (
              <div className="space-y-3">
                {photoPreview && (
                  <div className="rounded-2xl overflow-hidden">
                    <img src={photoPreview} alt="" className="w-full aspect-[4/3] object-cover" />
                  </div>
                )}
                <div className="card-elevated p-4 border-sky-glow/30">
                  <div className="flex items-center gap-2 mb-3">
                    <Tag size={16} className="text-sky-glow" />
                    <span className="font-display font-semibold text-sm">Этикетка распознана</span>
                  </div>
                  <label className="label mb-2 block">Название</label>
                  <input
                    type="text"
                    maxLength={100}
                    placeholder="Например: йогурт Epica клубника"
                    className={`input text-sm mb-1.5 ${labelName.trim() ? '' : 'border-warn/60'}`}
                    value={labelName}
                    onChange={(e) => setLabelName(e.target.value)}
                  />
                  <p className={`text-[11px] mb-3 ${labelName.trim() ? 'text-ink-dim' : 'text-warn'}`}>
                    {labelName.trim()
                      ? 'Можно поправить — по названию ты найдёшь продукт в истории'
                      : 'Названия в кадре не видно. Впиши своё — иначе в истории он будет просто «Продукт»'}
                  </p>
                  <div className="text-xs text-ink-muted mb-3">
                    На {labelData.per_grams || 100}г: <span className="text-ink">{Math.round(labelData.calories)} ккал · Б{Number(labelData.protein_g).toFixed(1)} · Ж{Number(labelData.fat_g).toFixed(1)} · У{Number(labelData.carbs_g).toFixed(1)}</span>
                  </div>
                  <label className="label mb-2 block">Сколько грамм ты съел?</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      placeholder="например, 50"
                      className="input flex-1 text-sm"
                      value={labelWeight}
                      onChange={(e) => setLabelWeight(e.target.value.replace(/\D/g, ''))}
                      autoFocus={!!labelName.trim()}
                    />
                    <Button
                      size="md"
                      onClick={applyLabelData}
                      disabled={!labelWeight}
                    >
                      Записать
                    </Button>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setLabelData(null); setPhotoPreview(null); setPhotoMode(null); }}
                    className="w-full mt-3 text-xs text-ink-dim hover:text-ink"
                  >
                    Сфотографировать заново
                  </button>
                </div>
              </div>
            )}
            {photoPreview && !analyzing && !labelData && (
              <>
                <div className="relative rounded-2xl overflow-hidden mb-3">
                  <img src={photoPreview} alt="" className="w-full aspect-[4/3] object-cover" />
                  {confidence !== undefined && (
                    <div className="absolute bottom-3 left-3 bg-bg/80 backdrop-blur px-3 py-1 rounded-full text-xs">
                      <Wand2 size={12} className="inline mr-1 text-emerald-glow" />
                      Уверенность ИИ: {Math.round(confidence * 100)}%
                    </div>
                  )}
                </div>

                {/* Поделиться — СРАЗУ ПОД ФОТО, до списка продуктов.
                    Стояло под «Итого», то есть ниже всех позиций, и Слава
                    трижды сообщал «кнопка пропала». Причина оказалась не
                    в состоянии и не в вёрстке: кнопка «Сохранить» ПЛАВАЮЩАЯ
                    (fixed bottom-16), поэтому доскроллить до конца списка
                    в обычном потоке незачем — человек снимает, смотрит
                    позиции и жмёт «Сохранить», не доезжая до низа. Один раз
                    Слава специально искал, доскроллил и поделился (23:06,
                    два репоста в базе) — отсюда и ощущение «то есть, то нет».
                    Здесь блок видно всегда: сразу после снимка, где и восторг.
                    Цифры живые — берутся из `totals`, поэтому правка позиций
                    отражается в карточке до самой отправки. */}
                {mode === 'photo' && photoMode === 'dish' && (
                  <ShareRecognition
                    facts={{
                      photo: photoPreview,
                      title: mealTitle(items),
                      calories: totals.cal,
                      protein: totals.p,
                      fat: totals.f,
                      carbs: totals.c,
                      seconds: analyzeSeconds,
                    }}
                  />
                )}

                {!showAddExtra ? (
                  <button
                    type="button"
                    onClick={() => setShowAddExtra(true)}
                    className="w-full py-2.5 rounded-xl border border-dashed border-line text-ink-dim text-sm flex items-center justify-center gap-2 hover:border-emerald-glow/50 hover:text-emerald-glow transition-colors mb-2"
                  >
                    <Plus size={14} /> Добавить продукт вручную
                  </button>
                ) : (
                  <div className="card p-3 mb-3">
                    <div className="text-xs text-ink-muted mb-2">Добавить продукт</div>
                    <input
                      className="input mb-2 text-sm"
                      placeholder="Название"
                      value={extraName}
                      onChange={(e) => setExtraName(e.target.value)}
                      autoFocus
                    />
                    <div className="flex gap-2">
                      <input
                        className="input text-sm w-28"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        placeholder="Вес, г"
                        value={extraWeight}
                        onChange={(e) => setExtraWeight(e.target.value.replace(/\D/g, ''))}
                      />
                      <Button
                        size="sm"
                        onClick={addExtraItem}
                        disabled={!extraName.trim() || analyzing}
                        className="flex-1"
                      >
                        {analyzing ? '…' : 'Добавить'}
                      </Button>
                      <button
                        type="button"
                        onClick={() => setShowAddExtra(false)}
                        className="text-ink-dim text-sm px-2"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </motion.div>
        )}
        {/* TEXT */}
        {mode === 'text' && (
          <motion.div key="text" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            {/* Готовое — ПЕРЕД полем ввода: человек, который 67 раз набирал
                «банан», должен сначала увидеть его тут, а не начинать печатать. */}
            <FrequentItems mealType={mealType} onPick={addFrequentItem} hideNames={items.map((i: any) => i.name)} />
            <label className="label mb-2 block">Опиши что съел или перечисли ингредиенты</label>
            <textarea
              className="input min-h-[140px] resize-none"
              placeholder={`Примеры:\n• гречка варёная 150г, куриная грудка 120г\n• 2 яйца, овсянка сухая 50г с бананом\n• борщ: картошка 150г, свёкла 100г, мясо 80г`}
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
            />
            <div className="mt-2 mb-3 text-xs text-ink-dim space-y-1">
              <div>💡 Пиши с ингредиентами и граммами — ИИ посчитает точнее</div>
              {/* Сухая и варёная крупа отличаются по калориям втрое, а из «гречка
                  100 г» этого не понять. Решение Славы 11.09.2026: ввод еды
                  переведён на младшую модель, и состояние крупы указывает сам
                  человек. Без пометки модель считает готовое — так в промпте. */}
              <div>🍚 Крупы и макароны: пиши «варёная» или «сухая». Без пометки считаем варёную</div>
            </div>
            <Button
              fullWidth
              size="md"
              disabled={!textInput.trim() || analyzing}
              onClick={handleTextAnalyze}
              icon={<Sparkles size={16} />}
            >
              {analyzing ? 'Анализирую…' : 'Распознать с ИИ'}
            </Button>
          </motion.div>
        )}
        {/* MANUAL */}
        {mode === 'manual' && (
          <motion.div key="manual" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <FrequentItems mealType={mealType} onPick={addFrequentItem} hideNames={items.map((i: any) => i.name)} />
            <label className="label mb-2 block">Название продукта</label>
            <input
              className="input mb-3"
              placeholder="Куриная грудка"
              value={manualName}
              onChange={(e) => setManualName(e.target.value)}
            />
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div>
                <label className="label mb-2 block">Вес, г</label>
                <input
                  className="input"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={manualWeight}
                  onChange={(e) => setManualWeight(e.target.value.replace(/\D/g, ''))}
                />
              </div>
              <div>
                <label className="label mb-2 block">Калории</label>
                <input
                  className="input"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="200"
                  value={manualCal}
                  onChange={(e) => setManualCal(e.target.value.replace(/\D/g, ''))}
                />
              </div>
            </div>
            <Button
              fullWidth
              variant="secondary"
              onClick={addManualItem}
              icon={<Plus size={16} />}
              disabled={!manualName || !manualCal}
            >
              Добавить продукт
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showAccuracyHint && items.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="mt-4 card border-sky-glow/30 bg-sky-glow/5 p-3 flex gap-2"
          >
            <Info size={16} className="text-sky-glow shrink-0 mt-0.5" />
            <div className="text-xs text-ink-muted">
              💡 Если AI неправильно распознал — тапни <Edit3 size={11} className="inline mx-0.5" /> на карточке чтобы изменить через AI
            </div>
            <button
              type="button"
              onClick={() => setShowAccuracyHint(false)}
              className="text-ink-dim text-xs ml-auto"
            >
              ОК
            </button>
          </motion.div>
        )}
      </AnimatePresence>
      {items.length > 0 && (
        <div className="mt-5">
          <div className="label mb-3">Продукты ({items.length})</div>
          <div className="space-y-2">
            {items.map((it, idx) => (
              <div key={idx} className="card p-3">
                <div className="flex items-center gap-2 mb-2">
                  {/* Название — поле, а не подпись: безымянному продукту подсвечиваем
                      рамку и подсказываем, зачем его назвать (11.10.2026). */}
                  <input
                    type="text"
                    value={it.name}
                    maxLength={100}
                    placeholder="Название продукта"
                    aria-label="Название продукта"
                    onFocus={(e) => { if (isNameless(it.name)) e.target.select(); }}
                    onChange={(e) => updateItemName(idx, e.target.value)}
                    className={`input py-1.5 px-2 text-sm flex-1 font-medium ${isNameless(it.name) ? 'border-warn/60' : ''}`}
                  />
                  {/* Кнопка редактирования через AI */}
                  <button
                    type="button"
                    onClick={() => {
                      haptic('light');
                      setEditingItem({ item: it, index: idx });
                    }}
                    className="text-ink-dim hover:text-emerald-glow shrink-0 p-1"
                    title="Изменить через AI"
                  >
                    <Edit3 size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeItem(idx)}
                    className="text-ink-dim hover:text-danger shrink-0 p-1"
                    title="Удалить"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={it.weight_g}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, '');
                      updateItemWeight(idx, Number(v) || 0);
                    }}
                    className="input py-1.5 px-2 w-24 text-sm"
                  />
                  <span className="text-xs text-ink-muted">г</span>
                  <span className="ml-auto font-display font-semibold">
                    {it.calories}{' '}
                    <span className="text-xs text-ink-dim font-body">ккал</span>
                  </span>
                </div>
                <div className="flex gap-3 text-[11px]">
                  <span className="text-emerald-glow">Б {fmtNum(Number(it.protein_g), 1)}</span>
                  <span className="text-warn">Ж {fmtNum(Number(it.fat_g), 1)}</span>
                  <span className="text-sky-glow">У {fmtNum(Number(it.carbs_g), 1)}</span>
                </div>
                {/* 🆕 Inline-редактор компонентов для составных блюд */}
                {/* Показываем если есть components ИЛИ если блюдо распознано через AI */}
                {(Array.isArray((it as any).components)) && (
                  <ComponentsEditor
                    components={(it as any).components || []}
                    onChange={(newComponents, recalcTotals) =>
                      handleComponentsChange(idx, newComponents, recalcTotals)
                    }
                  />
                )}
              </div>
            ))}
          </div>
          <div className="card-elevated mt-4 p-4 border-emerald-glow/30">
            <div className="flex items-baseline justify-between">
              <span className="label">Итого</span>
              <span className="font-display text-2xl font-bold text-emerald-glow">
                {fmtNum(totals.cal)}{' '}
                <span className="text-sm text-ink-muted font-body">ккал</span>
              </span>
            </div>
            <div className="flex gap-3 text-xs mt-2">
              <span className="text-emerald-glow">Б {fmtNum(totals.p, 1)}г</span>
              <span className="text-warn">Ж {fmtNum(totals.f, 1)}г</span>
              <span className="text-sky-glow">У {fmtNum(totals.c, 1)}г</span>
            </div>
          </div>

          <AccuracyNote />
        </div>
      )}
      <CameraCapture
        trigger={cameraTrigger}
        onClose={() => setPhotoMode(null)}
        onCapture={handlePhotoFromCamera}
      />
      {/* AI-редактор продукта */}
      {editingItem && (
        <MealItemEditor
          open={!!editingItem}
          item={editingItem.item}
          onClose={() => setEditingItem(null)}
          onSave={(updated) => handleEditorSave(updated, editingItem.index)}
          onDelete={() => handleEditorDelete(editingItem.index)}
        />
      )}
      {/* 🎯 STICKY КНОПКА СОХРАНЕНИЯ — всегда видна */}
      {hasItems && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="fixed bottom-16 left-0 right-0 z-40 px-5 pb-3 pt-3 bg-gradient-to-t from-bg via-bg/95 to-bg/0 max-w-md mx-auto"
        >
          <Button 
            fullWidth 
            size="lg" 
            disabled={saving} 
            onClick={save}
            className="shadow-2xl"
          >
            {saving 
              ? 'Сохраняем…' 
              : dayOffset !== 0
                ? `Сохранить ${dayPhrase(dayOffset)} · ${fmtNum(totals.cal)} ккал`
                : `Сохранить · ${fmtNum(totals.cal)} ккал`
            }
          </Button>
        </motion.div>
      )}
      {/* TOAST ПОСЛЕ СОХРАНЕНИЯ */}
      <AnimatePresence>
        {savedToast && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 50, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            className="fixed bottom-32 left-0 right-0 z-50 px-5 max-w-md mx-auto pointer-events-none"
          >
            <div className="pointer-events-auto">
              <div
                className="bg-emerald-glow text-bg rounded-2xl shadow-2xl px-5 py-4 flex items-center gap-3 cursor-pointer"
                onClick={() => setSavedToast(null)}
              >
                <div className="w-9 h-9 rounded-full bg-bg/15 flex items-center justify-center shrink-0">
                  <Check size={20} strokeWidth={3} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-sm">
                    Записано {savedToast.dayNote} · {mealTypeLabel[savedToast.mealType]}
                  </div>
                  <div className="text-xs opacity-80">
                    {fmtNum(savedToast.kcal)} ккал · можно добавить ещё
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    nav(savedToast.dayNote ? '/diary' : '/');
                  }}
                  className="text-xs font-bold underline shrink-0"
                >
                  Готово →
                </button>
              </div>

              {/* Реакция Славы на этот приём — там, где человек и так смотрит.
                  Отдельной строкой под тостом, а не поверх экрана: главное
                  действие не перекрыто (хартия ненавязчивости). Появляется
                  не всегда — молчать, когда сказать нечего, это правило. */}
              {/* Поделиться — ПОСЛЕ сохранения. Раньше блок стоял под «Итого»,
                  но кнопка «Сохранить» плавающая (fixed bottom-16) и висит
                  поверх содержимого: до карточки нельзя было доскроллить,
                  а после сохранения форма очищалась и блок исчезал вовсе.
                  Здесь момент правильный: приём записан, восторг свежий. */}
              {savedToast.reco && (
                <div className="mt-2">
                  <ShareRecognition facts={savedToast.reco} />
                </div>
              )}

              {savedToast.reaction && (
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 }}
                  className="mt-2 card px-3 py-2.5 flex items-center gap-2.5"
                  style={{ borderColor: 'rgba(16,229,142,0.28)' }}
                >
                  <div className="w-[26px] h-[26px] rounded-full bg-emerald-glow/15 flex items-center justify-center shrink-0">
                    <Sparkles size={14} className="text-emerald-glow" />
                  </div>
                  <div className="flex-1 min-w-0 text-[13px] leading-snug text-ink">
                    {savedToast.reaction.text}
                  </div>
                  <button
                    onClick={() => {
                      const ask = savedToast.reaction?.ask;
                      setSavedToast(null);
                      // Вопрос уходит в чат первой репликой — контекст уже собран,
                      // человеку не надо формулировать заново.
                      try { if (ask) sessionStorage.setItem('slava_prefill', ask); } catch {}
                      nav('/chat');
                    }}
                    className="shrink-0 px-2.5 py-1.5 rounded-lg text-xs font-medium text-emerald-glow border border-emerald-glow/35"
                  >
                    спросить
                  </button>
                </motion.div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
function getDefaultMealType(): MealType {
  const h = new Date().getHours();
  if (h < 11) return 'breakfast';
  if (h < 16) return 'lunch';
  if (h < 21) return 'dinner';
  return 'snack';
}
